export interface TenantUserRecord {
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  emailVerified?: boolean;
  enabled?: boolean;
  tenantMemberships?: Array<{
    tenantId: string;
    tenantName?: string;
    roles: string[];
    status?: string;
  }>;
}

export interface TenantUserListResponse {
  users: TenantUserRecord[];
  total: number;
}

export interface TenantMemberInvite {
  id: string;
  tenantId: string;
  email: string;
  roleCode: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked' | string;
  inviteToken: string;
  invitedByUserId?: string | null;
  acceptedByUserId?: string | null;
  acceptedMembershipId?: string | null;
  expiresAt?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt?: string | null;
}

interface TenantMemberInviteCollection {
  items?: TenantMemberInvite[];
}

export interface CreateTenantMemberInvitePayload {
  email: string;
  roleCode?: string;
  expiresAt?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface TenantMemberInviteRedeemResult {
  invite: TenantMemberInvite;
  membership?: {
    id: string;
    tenantId: string;
    userId: string;
    role: string;
  } | null;
}

export class TenantUsersError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'TenantUsersError';
    this.status = status;
  }
}

async function readJSON<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const body = await response.json() as { message?: string; detail?: string; title?: string };
      message = body.message || body.detail || body.title || message;
    } catch {
      // ignore
    }
    throw new TenantUsersError(message, response.status);
  }

  return readJSON<T>(response);
}

export async function listTenantUsers(tenantId: string): Promise<TenantUserRecord[]> {
  const payload = await request<TenantUserListResponse>(`/api/v1/tenants/${encodeURIComponent(tenantId)}/users`);
  return payload.users ?? [];
}

export async function listTenantMemberInvites(tenantId: string): Promise<TenantMemberInvite[]> {
  const payload = await request<TenantMemberInviteCollection>(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/member-invites`,
  );
  return payload.items ?? [];
}

export async function createTenantMemberInvite(tenantId: string, payload: CreateTenantMemberInvitePayload): Promise<TenantMemberInvite> {
  return request<TenantMemberInvite>(`/api/v1/tenants/${encodeURIComponent(tenantId)}/member-invites`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function revokeTenantMemberInvite(tenantId: string, inviteId: string): Promise<TenantMemberInvite> {
  return request<TenantMemberInvite>(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/member-invites/${encodeURIComponent(inviteId)}`,
    { method: 'DELETE' },
  );
}

export async function revokeTenantMember(tenantId: string, userId: string): Promise<void> {
  await request<void>(`/api/v1/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
}

export async function redeemTenantMemberInvite(inviteToken: string): Promise<TenantMemberInviteRedeemResult> {
  return request<TenantMemberInviteRedeemResult>('/api/v1/member-invites/redeem', {
    method: 'POST',
    body: JSON.stringify({ inviteToken }),
  });
}
